import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Hotel, Building2, User, Mail, Phone, MapPin, Globe, Lock, Eye, EyeOff, Loader2, CheckCircle, ArrowLeft, Star, Bed } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PROPERTY_TYPES = [
  { value: 'HOTEL', label: 'Hotel' },
  { value: 'RESORT', label: 'Resort' },
  { value: 'BOUTIQUE', label: 'Boutique Hotel' },
  { value: 'MOTEL', label: 'Motel' },
  { value: 'HOSTEL', label: 'Hostel' },
  { value: 'APARTMENT', label: 'Serviced Apartment' },
  { value: 'VILLA', label: 'Villa' },
  { value: 'BNB', label: 'Bed & Breakfast' },
];

const COUNTRIES = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'AU', label: 'Australia' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'ES', label: 'Spain' },
  { value: 'IT', label: 'Italy' },
  { value: 'JP', label: 'Japan' },
  { value: 'IN', label: 'India' },
  { value: 'AE', label: 'United Arab Emirates' },
  { value: 'SG', label: 'Singapore' },
  { value: 'TH', label: 'Thailand' },
  { value: 'MX', label: 'Mexico' },
  { value: 'BR', label: 'Brazil' },
  { value: 'ET', label: 'Ethiopia' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'NG', label: 'Nigeria' },
  { value: 'KE', label: 'Kenya' },
  { value: 'OTHER', label: 'Other' },
];

const CURRENCIES = [
  { value: 'USD', label: 'USD - US Dollar' },
  { value: 'EUR', label: 'EUR - Euro' },
  { value: 'GBP', label: 'GBP - British Pound' },
  { value: 'CAD', label: 'CAD - Canadian Dollar' },
  { value: 'AUD', label: 'AUD - Australian Dollar' },
  { value: 'JPY', label: 'JPY - Japanese Yen' },
  { value: 'INR', label: 'INR - Indian Rupee' },
  { value: 'AED', label: 'AED - UAE Dirham' },
  { value: 'SGD', label: 'SGD - Singapore Dollar' },
  { value: 'THB', label: 'THB - Thai Baht' },
  { value: 'ETB', label: 'ETB - Ethiopian Birr' },
  { value: 'ZAR', label: 'ZAR - South African Rand' },
  { value: 'NGN', label: 'NGN - Nigerian Naira' },
  { value: 'KES', label: 'KES - Kenyan Shilling' },
];

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (US & Canada)' },
  { value: 'America/Chicago', label: 'Central Time (US & Canada)' },
  { value: 'America/Denver', label: 'Mountain Time (US & Canada)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)' },
  { value: 'Europe/London', label: 'London' },
  { value: 'Europe/Paris', label: 'Paris' },
  { value: 'Europe/Berlin', label: 'Berlin' },
  { value: 'Asia/Dubai', label: 'Dubai' },
  { value: 'Asia/Singapore', label: 'Singapore' },
  { value: 'Asia/Tokyo', label: 'Tokyo' },
  { value: 'Asia/Kolkata', label: 'India' },
  { value: 'Australia/Sydney', label: 'Sydney' },
  { value: 'Africa/Addis_Ababa', label: 'Addis Ababa' },
  { value: 'Africa/Johannesburg', label: 'Johannesburg' },
  { value: 'Africa/Lagos', label: 'Lagos' },
  { value: 'Africa/Nairobi', label: 'Nairobi' },
];

export default function AdminRegisterPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    // Hotel Details
    hotel_name: '',
    hotel_code: '',
    property_type: 'HOTEL',
    star_rating: '',
    total_rooms: '',
    
    // Location
    address_street: '',
    address_city: '',
    address_state: '',
    address_country: 'US',
    timezone: 'America/New_York',
    
    // Contact
    hotel_email: '',
    hotel_phone: '',
    website: '',
    
    // Admin Details
    admin_full_name: '',
    admin_email: '',
    admin_password: '',
    confirm_password: '',
    admin_phone: '',
    
    // Business
    legal_name: '',
    tax_id: '',
    currency: 'USD',
    
    // Additional
    description: '',
    terms_accepted: false,
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.hotel_name.trim()) return 'Hotel name is required';
        if (!formData.hotel_code.trim()) return 'Hotel code is required';
        if (formData.hotel_code.length < 2 || formData.hotel_code.length > 10) return 'Hotel code must be 2-10 characters';
        if (!/^[A-Za-z0-9]+$/.test(formData.hotel_code)) return 'Hotel code must be alphanumeric only';
        if (!formData.hotel_email.trim()) return 'Hotel email is required';
        return null;
      case 2:
        if (!formData.address_city.trim()) return 'City is required';
        if (!formData.address_country) return 'Country is required';
        return null;
      case 3:
        if (!formData.admin_full_name.trim()) return 'Admin name is required';
        if (!formData.admin_email.trim()) return 'Admin email is required';
        if (!formData.admin_password) return 'Password is required';
        if (formData.admin_password.length < 8) return 'Password must be at least 8 characters';
        if (formData.admin_password !== formData.confirm_password) return 'Passwords do not match';
        return null;
      case 4:
        if (!formData.terms_accepted) return 'You must accept the terms and conditions';
        return null;
      default:
        return null;
    }
  };

  const handleNext = () => {
    const error = validateStep(currentStep);
    if (error) {
      setError(error);
      return;
    }
    setCurrentStep(prev => prev + 1);
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const error = validateStep(4);
    if (error) {
      setError(error);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/registrations/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          hotel_code: formData.hotel_code.toUpperCase(),
          star_rating: formData.star_rating ? parseInt(formData.star_rating) : null,
          total_rooms: formData.total_rooms ? parseInt(formData.total_rooms) : null,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }

      setSuccess(data);
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Success Screen
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-accent/10 p-4">
        <div className="w-full max-w-lg">
          <Card className="shadow-xl border-0">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Registration Submitted!</h2>
              <p className="text-muted-foreground mb-6">
                {success.message}
              </p>
              
              <div className="bg-muted/50 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm font-medium mb-2">Registration Details:</p>
                <div className="space-y-1 text-sm">
                  <p><span className="text-muted-foreground">Hotel:</span> {success.hotel_name}</p>
                  <p><span className="text-muted-foreground">Code:</span> {success.hotel_code}</p>
                  <p><span className="text-muted-foreground">Status:</span> <Badge variant="outline" className="text-amber-600 border-amber-300">Pending Approval</Badge></p>
                  <p><span className="text-muted-foreground">Reference ID:</span> <code className="text-xs bg-muted px-1 rounded">{success.id}</code></p>
                </div>
              </div>

              <p className="text-sm text-muted-foreground mb-6">
                You will receive an email notification once your application has been reviewed.
              </p>

              <div className="space-y-2">
                <Button onClick={() => navigate('/login')} className="w-full">
                  Go to Login
                </Button>
                <Button variant="outline" onClick={() => {
                  setSuccess(null);
                  setCurrentStep(1);
                  setFormData({
                    hotel_name: '', hotel_code: '', property_type: 'HOTEL', star_rating: '', total_rooms: '',
                    address_street: '', address_city: '', address_state: '', address_country: 'US', timezone: 'America/New_York',
                    hotel_email: '', hotel_phone: '', website: '',
                    admin_full_name: '', admin_email: '', admin_password: '', confirm_password: '', admin_phone: '',
                    legal_name: '', tax_id: '', currency: 'USD', description: '', terms_accepted: false,
                  });
                }} className="w-full">
                  Register Another Hotel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Step Indicator
  const StepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3, 4].map((step) => (
        <React.Fragment key={step}>
          <div 
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
              step === currentStep 
                ? 'bg-primary text-primary-foreground' 
                : step < currentStep 
                  ? 'bg-primary/20 text-primary' 
                  : 'bg-muted text-muted-foreground'
            }`}
          >
            {step < currentStep ? '✓' : step}
          </div>
          {step < 4 && (
            <div className={`w-12 h-0.5 mx-1 ${step < currentStep ? 'bg-primary' : 'bg-muted'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-background to-accent/10 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <Link to="/login" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-4">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Login
          </Link>
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary mb-4">
            <Hotel className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-admin-heading)' }}>
            Register Your Hotel
          </h1>
          <p className="text-muted-foreground mt-1">Start managing your property with HMS</p>
        </div>

        <StepIndicator />

        <Card className="shadow-xl border-0">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">
              {currentStep === 1 && 'Hotel Information'}
              {currentStep === 2 && 'Location & Contact'}
              {currentStep === 3 && 'Administrator Account'}
              {currentStep === 4 && 'Review & Submit'}
            </CardTitle>
            <CardDescription>
              {currentStep === 1 && 'Tell us about your property'}
              {currentStep === 2 && 'Where is your hotel located?'}
              {currentStep === 3 && 'Create your admin account'}
              {currentStep === 4 && 'Verify your information and submit'}
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit}>
              {error && (
                <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm mb-4" data-testid="register-error">
                  {error}
                </div>
              )}

              {/* Step 1: Hotel Information */}
              {currentStep === 1 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label htmlFor="hotel_name">Hotel Name *</Label>
                      <Input
                        id="hotel_name"
                        placeholder="Grand Hotel"
                        value={formData.hotel_name}
                        onChange={(e) => handleChange('hotel_name', e.target.value)}
                        data-testid="hotel-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="hotel_code">Hotel Code *</Label>
                      <Input
                        id="hotel_code"
                        placeholder="GRNDHTL"
                        value={formData.hotel_code}
                        onChange={(e) => handleChange('hotel_code', e.target.value.toUpperCase())}
                        maxLength={10}
                        className="uppercase"
                        data-testid="hotel-code-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">2-10 alphanumeric characters</p>
                    </div>
                    <div>
                      <Label htmlFor="property_type">Property Type</Label>
                      <Select value={formData.property_type} onValueChange={(v) => handleChange('property_type', v)}>
                        <SelectTrigger data-testid="property-type-select">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {PROPERTY_TYPES.map(type => (
                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="star_rating">Star Rating</Label>
                      <Select value={formData.star_rating} onValueChange={(v) => handleChange('star_rating', v)}>
                        <SelectTrigger data-testid="star-rating-select">
                          <SelectValue placeholder="Select rating" />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5].map(rating => (
                            <SelectItem key={rating} value={String(rating)}>
                              <span className="flex items-center">
                                {rating} <Star className="w-3 h-3 ml-1 fill-amber-400 text-amber-400" />
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="total_rooms">Total Rooms</Label>
                      <div className="relative">
                        <Bed className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="total_rooms"
                          type="number"
                          placeholder="50"
                          value={formData.total_rooms}
                          onChange={(e) => handleChange('total_rooms', e.target.value)}
                          className="pl-9"
                          min={1}
                          data-testid="total-rooms-input"
                        />
                      </div>
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="hotel_email">Hotel Email *</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="hotel_email"
                          type="email"
                          placeholder="info@grandhotel.com"
                          value={formData.hotel_email}
                          onChange={(e) => handleChange('hotel_email', e.target.value)}
                          className="pl-9"
                          data-testid="hotel-email-input"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Location & Contact */}
              {currentStep === 2 && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="address_street">Street Address</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="address_street"
                        placeholder="123 Main Street"
                        value={formData.address_street}
                        onChange={(e) => handleChange('address_street', e.target.value)}
                        className="pl-9"
                        data-testid="address-street-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="address_city">City *</Label>
                      <Input
                        id="address_city"
                        placeholder="New York"
                        value={formData.address_city}
                        onChange={(e) => handleChange('address_city', e.target.value)}
                        data-testid="address-city-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="address_state">State/Province</Label>
                      <Input
                        id="address_state"
                        placeholder="NY"
                        value={formData.address_state}
                        onChange={(e) => handleChange('address_state', e.target.value)}
                        data-testid="address-state-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="address_country">Country *</Label>
                      <Select value={formData.address_country} onValueChange={(v) => handleChange('address_country', v)}>
                        <SelectTrigger data-testid="country-select">
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRIES.map(country => (
                            <SelectItem key={country.value} value={country.value}>{country.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="timezone">Timezone</Label>
                      <Select value={formData.timezone} onValueChange={(v) => handleChange('timezone', v)}>
                        <SelectTrigger data-testid="timezone-select">
                          <SelectValue placeholder="Select timezone" />
                        </SelectTrigger>
                        <SelectContent>
                          {TIMEZONES.map(tz => (
                            <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="hotel_phone">Phone</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="hotel_phone"
                          placeholder="+1 (555) 123-4567"
                          value={formData.hotel_phone}
                          onChange={(e) => handleChange('hotel_phone', e.target.value)}
                          className="pl-9"
                          data-testid="hotel-phone-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="website">Website</Label>
                      <div className="relative">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="website"
                          placeholder="www.grandhotel.com"
                          value={formData.website}
                          onChange={(e) => handleChange('website', e.target.value)}
                          className="pl-9"
                          data-testid="website-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="currency">Currency</Label>
                      <Select value={formData.currency} onValueChange={(v) => handleChange('currency', v)}>
                        <SelectTrigger data-testid="currency-select">
                          <SelectValue placeholder="Select currency" />
                        </SelectTrigger>
                        <SelectContent>
                          {CURRENCIES.map(curr => (
                            <SelectItem key={curr.value} value={curr.value}>{curr.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Admin Account */}
              {currentStep === 3 && (
                <div className="space-y-4">
                  <div className="p-3 rounded-lg bg-primary/5 text-sm mb-4">
                    <p className="font-medium">Create your administrator account</p>
                    <p className="text-muted-foreground">This account will have full access to manage your hotel.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label htmlFor="admin_full_name">Full Name *</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="admin_full_name"
                          placeholder="John Smith"
                          value={formData.admin_full_name}
                          onChange={(e) => handleChange('admin_full_name', e.target.value)}
                          className="pl-9"
                          data-testid="admin-name-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="admin_email">Email *</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="admin_email"
                          type="email"
                          placeholder="admin@grandhotel.com"
                          value={formData.admin_email}
                          onChange={(e) => handleChange('admin_email', e.target.value)}
                          className="pl-9"
                          data-testid="admin-email-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="admin_phone">Phone</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="admin_phone"
                          placeholder="+1 (555) 123-4567"
                          value={formData.admin_phone}
                          onChange={(e) => handleChange('admin_phone', e.target.value)}
                          className="pl-9"
                          data-testid="admin-phone-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="admin_password">Password *</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="admin_password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={formData.admin_password}
                          onChange={(e) => handleChange('admin_password', e.target.value)}
                          className="pl-9 pr-10"
                          data-testid="admin-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">At least 8 characters</p>
                    </div>
                    <div>
                      <Label htmlFor="confirm_password">Confirm Password *</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="confirm_password"
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={formData.confirm_password}
                          onChange={(e) => handleChange('confirm_password', e.target.value)}
                          className="pl-9 pr-10"
                          data-testid="confirm-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Review & Submit */}
              {currentStep === 4 && (
                <div className="space-y-4">
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg border">
                      <h4 className="font-medium flex items-center mb-2">
                        <Building2 className="w-4 h-4 mr-2" />
                        Hotel Information
                      </h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div><span className="text-muted-foreground">Name:</span> {formData.hotel_name}</div>
                        <div><span className="text-muted-foreground">Code:</span> {formData.hotel_code.toUpperCase()}</div>
                        <div><span className="text-muted-foreground">Type:</span> {PROPERTY_TYPES.find(t => t.value === formData.property_type)?.label}</div>
                        <div><span className="text-muted-foreground">Rating:</span> {formData.star_rating ? `${formData.star_rating} Star` : 'Not set'}</div>
                        <div><span className="text-muted-foreground">Rooms:</span> {formData.total_rooms || 'Not set'}</div>
                        <div><span className="text-muted-foreground">Email:</span> {formData.hotel_email}</div>
                      </div>
                    </div>

                    <div className="p-4 rounded-lg border">
                      <h4 className="font-medium flex items-center mb-2">
                        <MapPin className="w-4 h-4 mr-2" />
                        Location
                      </h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="col-span-2"><span className="text-muted-foreground">Address:</span> {formData.address_street || 'Not set'}</div>
                        <div><span className="text-muted-foreground">City:</span> {formData.address_city}</div>
                        <div><span className="text-muted-foreground">State:</span> {formData.address_state || 'Not set'}</div>
                        <div><span className="text-muted-foreground">Country:</span> {COUNTRIES.find(c => c.value === formData.address_country)?.label}</div>
                        <div><span className="text-muted-foreground">Currency:</span> {formData.currency}</div>
                      </div>
                    </div>

                    <div className="p-4 rounded-lg border">
                      <h4 className="font-medium flex items-center mb-2">
                        <User className="w-4 h-4 mr-2" />
                        Administrator
                      </h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div><span className="text-muted-foreground">Name:</span> {formData.admin_full_name}</div>
                        <div><span className="text-muted-foreground">Email:</span> {formData.admin_email}</div>
                        <div><span className="text-muted-foreground">Phone:</span> {formData.admin_phone || 'Not set'}</div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 pt-4">
                    <Checkbox
                      id="terms"
                      checked={formData.terms_accepted}
                      onCheckedChange={(checked) => handleChange('terms_accepted', checked)}
                      data-testid="terms-checkbox"
                    />
                    <label htmlFor="terms" className="text-sm leading-tight cursor-pointer">
                      I agree to the <a href="#" className="text-primary hover:underline">Terms of Service</a> and{' '}
                      <a href="#" className="text-primary hover:underline">Privacy Policy</a>. I understand that my registration is subject to approval.
                    </label>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-6">
                {currentStep > 1 ? (
                  <Button type="button" variant="outline" onClick={handleBack}>
                    Back
                  </Button>
                ) : (
                  <div />
                )}
                
                {currentStep < 4 ? (
                  <Button type="button" onClick={handleNext} data-testid="next-step-btn">
                    Continue
                  </Button>
                ) : (
                  <Button type="submit" disabled={isLoading} data-testid="submit-registration-btn">
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Submit Registration'
                    )}
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Already have an account? <Link to="/login" className="text-primary hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
